import React from "react";
import { SignOutButton } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/authentication";
import { useGetAdminStatusQuery, useGetUnreadCountQuery } from "../../store/slices/api";
import useMessageNotifications from "../../hooks/useMessageNotifications";
import NavigationTab from "./NavigationTab";

import LogOutSvg from "../../assets/LogOut";
import HomeSvg from "../../assets/Home";
import NetworkSvg from "../../assets/Network";
import MegaphoneSvg from "../../assets/Megaphone";
import StoreSvg from "../../assets/Store";
import HandshakeSvg from "../../assets/Handshake";
import SettingsSvg from "../../assets/Settings";
import ShieldSvg from "../../assets/Shield";
import ChatBubbleSvg from "../../assets/ChatBubble";

function Sidebar() {
  const user = useSelector(selectUser);
  const individualId = user?.externalId ? parseInt(user.externalId) : null;

  const { data: adminData } = useGetAdminStatusQuery(individualId, {
    skip: !individualId,
  });
  const isAdmin = adminData?.is_admin || false;

  const { data: unreadData } = useGetUnreadCountQuery(individualId, {
    skip: !individualId,
    // Poll less often (30s, was 10s) and stop polling entirely while the tab
    // isn't focused -- cuts the steady background load that scaled with the
    // number of people signed in, not the number actually using the site.
    pollingInterval: 30000,
    skipPollingIfUnfocused: true,
  });
  const unreadCount = unreadData?.unread_count || 0;

  // Play chime / show browser notification when new mail arrives
  useMessageNotifications(unreadCount);


  return (
    <div className="flex flex-col h-full w-[300px] bg-dupontBlue justify-between">
      <div className="flex flex-col w-full gap-y-[10px]">
        {/* The name at the top is a link home. */}
        <Link
          to="/"
          className="flex flex-col items-center justify-center py-[28px] px-[20px] cursor-pointer hover:opacity-80 transition-opacity no-underline"
        >
          <h1 className="text-[36px] font-blackletter text-white text-center leading-tight">
            du Pont Circle
          </h1>
          <div className="w-[60px] h-[1px] bg-dupontGoldLt mt-[10px]"></div>
        </Link>
        <div className="flex flex-col w-full">
          <NavigationTab
            label="Home"
            icon={<HomeSvg fill="#FFFFFF" width={18} height={18} />}
            path="/"
          />
          <NavigationTab
            label="Family Tree"
            icon={<NetworkSvg fill="#FFFFFF" width={18} height={18} />}
            path="/tree"
          />
          <NavigationTab
            label="Bulletin & Blog"
            icon={<MegaphoneSvg fill="#FFFFFF" width={18} height={18} />}
            path="/bulletin"
            alsoActiveOn={["/footprint"]}
          />
          <NavigationTab
            label="Marketplace"
            icon={<StoreSvg fill="#FFFFFF" width={18} height={18} />}
            path="/marketplace"
          />
          {/* The Blog now lives inside the Bulletin, and Correspondence inside
              the hub (Sam, 2026-09-26) -- so the new-letters count sits here, and
              with new letters waiting the item opens straight onto them. */}
          <NavigationTab
            label="Family Connect Hub"
            icon={<HandshakeSvg fill="#FFFFFF" width={18} height={18} />}
            path={unreadCount > 0 ? "/connect?tab=correspondence" : "/connect"}
            activePath="/connect"
            alsoActiveOn={["/scrapbook", "/messages"]}
            tag={
              unreadCount > 0 ? (
                <span className="bg-dupontGold text-white text-[11px] font-bold rounded-full w-[20px] h-[20px] flex items-center justify-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              ) : null
            }
          />
          <NavigationTab
            label="Ask AI"
            icon={<ChatBubbleSvg fill="#FFFFFF" width={18} height={18} />}
            path="/ask-ai"
          />
          <NavigationTab
            label="Settings"
            icon={<SettingsSvg fill="#FFFFFF" width={18} height={18} />}
            path="/settings"
          />
          {isAdmin && (
            <NavigationTab
              label="Admin"
              icon={<ShieldSvg fill="#FFFFFF" width={18} height={18} />}
              path="/admin"
            />
          )}
        </div>
      </div>
      <div className="p-[20px]">
        <div className="text-white flex flex-row items-center gap-x-[10px]">
          <LogOutSvg fill="white" width={18} height={18} />
          <SignOutButton />
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
